import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Sparkles as SparklesIcon, ArrowRight } from "lucide-react";
import { WaitlistDialog } from "./WaitlistDialog";
import { BeforeAfterSlider } from "./BeforeAfterSlider";
import { Sparkles } from "./Sparkles";
import { MagneticButton } from "./MagneticButton";
import { useLanguage } from "@/contexts/LanguageContext";
import { useIsDesktop, usePrefersReducedMotion } from "@/hooks/useMediaQuery";
const heroOriginal = "/hero-original.webp";
const heroAvatar = "/hero-avatar.webp";
import blackBlazer from "@/assets/black-blazer.webp";

export function HeroSection() {
  const { t } = useLanguage();
  const isDesktop = useIsDesktop();
  const reduced = usePrefersReducedMotion();
  const parallaxEnabled = isDesktop && !reduced;

  const sectionRef = useRef<HTMLElement>(null);
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start start", "end start"],
  });
  const visualY = useTransform(scrollYProgress, [0, 1], [0, -60]);

  return (
    <section ref={sectionRef} className="relative min-h-screen flex items-center pt-20 overflow-hidden">
      {/* Mesh gradient background */}
      <div className="mesh-gradient" />
      
      {/* Animated orbs - hidden on iOS for smoother scroll */}
      <motion.div 
        animate={{ 
          x: [0, 30, 0],
          y: [0, -20, 0],
        }}
        transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
        className="absolute top-1/4 left-1/4 w-[500px] h-[500px] rounded-full blur-[120px] opacity-30 hidden-on-ios"
        style={{ background: 'radial-gradient(circle, hsl(280 100% 70% / 0.4), transparent 70%)' }}
      />
      <motion.div 
        animate={{ 
          x: [0, -40, 0],
          y: [0, 30, 0],
        }}
        transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
        className="absolute bottom-1/4 right-1/4 w-[400px] h-[400px] rounded-full blur-[100px] opacity-25 hidden-on-ios"
        style={{ background: 'radial-gradient(circle, hsl(200 100% 70% / 0.4), transparent 70%)' }}
      />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-8 py-12 lg:py-0">
        <div className="grid lg:grid-cols-2 gap-16 lg:gap-20 items-center">
          {/* Left Content */}
          <motion.div
            initial="hidden"
            animate="visible"
            variants={{
              hidden: {},
              visible: { transition: { staggerChildren: 0.05 } },
            }}
            className="text-center lg:text-left"
          >
            {/* Badge */}
            <motion.div
              variants={{ hidden: { opacity: 0, y: 20 }, visible: { opacity: 1, y: 0 } }}
              transition={{ duration: 0.5 }}
              className="pill-badge mb-8"
            >
              <SparklesIcon className="w-4 h-4 text-foreground/70" />
              <span className="text-sm font-medium text-foreground/80">
                {t('hero.badge')}
              </span>
            </motion.div>

            {/* Headline */}
            <motion.h1
              variants={{ hidden: { opacity: 0, y: 30 }, visible: { opacity: 1, y: 0 } }}
              transition={{ duration: 0.6 }}
              className="relative text-5xl md:text-6xl lg:text-7xl font-semibold leading-[1.05] mb-6 tracking-tight"
            >
              {t('hero.title').split(' ').slice(0, -2).join(' ')}
              <br />
              <span className="relative inline-block">
                <span className="text-gradient-rainbow">{t('hero.title').split(' ').slice(-2).join(' ')}</span>
                <Sparkles count={10} />
              </span>
            </motion.h1>

            {/* Subtext */}
            <motion.p
              variants={{ hidden: { opacity: 0, y: 20 }, visible: { opacity: 1, y: 0 } }}
              transition={{ duration: 0.5 }}
              className="text-lg md:text-xl text-muted-foreground max-w-lg mx-auto lg:mx-0 mb-10 leading-relaxed"
            >
              {t('hero.subtitle')}
            </motion.p>

            {/* Buttons */}
            <motion.div
              variants={{ hidden: { opacity: 0, y: 20 }, visible: { opacity: 1, y: 0 } }}
              transition={{ duration: 0.5 }}
              className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start"
            >
              <WaitlistDialog>
                <MagneticButton>
                  <motion.button
                    whileHover={{ scale: 1.05, boxShadow: "0 8px 30px hsl(0 0% 0% / 0.15)" }}
                    whileTap={{ scale: 0.97 }}
                    transition={{ type: "spring", stiffness: 400, damping: 17 }}
                    className="btn-primary text-lg flex items-center justify-center gap-2"
                  >
                    {t('hero.cta.primary')}
                    <ArrowRight className="w-5 h-5" />
                  </motion.button>
                </MagneticButton>
              </WaitlistDialog>

              <motion.a
                href="#technology"
                whileHover={{ scale: 1.05, boxShadow: "0 8px 30px hsl(0 0% 0% / 0.1)" }}
                whileTap={{ scale: 0.97 }}
                transition={{ type: "spring", stiffness: 400, damping: 17 }}
                className="btn-secondary text-lg"
              >
                {t('hero.cta.secondary')}
              </motion.a>
            </motion.div>

            {/* Mobile store badges */}
            <motion.div
              variants={{ hidden: { opacity: 0, y: 10 }, visible: { opacity: 1, y: 0 } }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="mt-5 flex flex-wrap items-center gap-2 justify-center lg:justify-start"
            >
              <span className="px-3 py-1.5 rounded-full text-xs font-medium liquid-glass text-foreground/70">
                {t('hero.store.appStore')}
              </span>
              <span className="px-3 py-1.5 rounded-full text-xs font-medium liquid-glass text-foreground/70">
                {t('hero.store.googlePlay')}
              </span>
            </motion.div>

            {/* Trust badges */}
            <motion.div
              variants={{ hidden: { opacity: 0, y: 10 }, visible: { opacity: 1, y: 0 } }}
              transition={{ duration: 0.5, delay: 0.15 }}
              className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2 justify-center lg:justify-start text-xs text-muted-foreground"
            >
              <span>{t('hero.trust.location')}</span>
              <span className="opacity-30">·</span>
              <span>{t('hero.trust.gemini')}</span>
              <span className="opacity-30">·</span>
              <span>{t('hero.trust.launch')}</span>
            </motion.div>
          </motion.div>

          {/* Right Visual - Comparison */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.4, ease: [0.25, 0.1, 0.25, 1] }}
            style={parallaxEnabled ? { y: visualY } : undefined}
            className="relative"
          >
            <div className="relative aspect-[4/5] max-w-md mx-auto">
              {/* Rainbow glow behind */}
              <div className="absolute -inset-8 rounded-[2.5rem] opacity-40 blur-3xl -z-10 animate-glow-pulse"
                style={{ background: 'linear-gradient(135deg, hsl(280 100% 70% / 0.3), hsl(200 100% 70% / 0.3), hsl(160 100% 60% / 0.3))' }}
              />
              
              {/* Comparison Container */}
              <div className="relative w-full h-full rounded-[2rem] overflow-hidden liquid-glass-rainbow">
                <BeforeAfterSlider
                  beforeImage={heroOriginal}
                  afterImage={heroAvatar}
                  beforeLabel={t('hero.original')}
                  afterLabel={t('hero.avatar')}
                />
              </div>

              {/* Floating Card */}
              <motion.div
                initial={{ opacity: 0, y: 20, x: 20 }}
                animate={{ opacity: 1, y: 0, x: 0 }}
                transition={{ delay: 1, duration: 0.8 }}
                className="absolute right-3 sm:-right-4 top-1/3 floating-element max-w-[180px]"
              >
                <div className="liquid-glass p-3 sm:p-4 shadow-2xl">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-xl overflow-hidden">
                      <img
                        src={blackBlazer}
                        alt="Blazer"
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-foreground">
                        {t('hero.blackBlazer')}
                      </p>
                      <p className="text-xs text-muted-foreground">{t('hero.wearingNow')}</p>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}